import { DAY_MS } from '@/lib/sources';

/**
 * The default aircraft, and the schedule the startup check runs on.
 *
 * Most pilots in this app's audience fly one aeroplane — their own, or the one
 * on the club line they always book — and for them the whole search screen is
 * ceremony: same registration, same source, every time. So the aircraft
 * becomes a setting, and opening the app looks for what it has flown since the
 * last look.
 *
 * What that must *not* become is a background job that spends money. Three
 * rules, and every number below exists to keep one of them:
 *
 *  - **Opt in, never on by default.** `enabled` starts false and only a
 *    deliberate tick turns it on, with the source's own cost estimate next to
 *    the tick.
 *  - **One check, not a poll.** `MIN_CHECK_INTERVAL_HOURS` means opening six
 *    tabs, or reloading while you work, costs one search a quarter-day — not
 *    one per page load.
 *  - **A bounded window, and nothing downloaded.** A pilot who last opened the
 *    app in March does not want April through September searched in one go, so
 *    the window is clamped to `MAX_LOOKBACK_DAYS`. And the check downloads
 *    nothing at all: it lists what it found, and the pilot fetches the ones they
 *    were flying. An earlier version of this fetched five of them unasked, which
 *    is how you end up deleting four flights somebody else flew.
 *
 * Keys are `autoImport.*` rows in the same settings table as everything else —
 * `Setting` is key-value precisely so a new feature is new rows, not a
 * migration.
 */

export const AUTO_IMPORT_KEYS = {
  /** The pilot's default aircraft — an `Aircraft.id`, or absent for none. */
  aircraftId: 'autoImport.aircraftId',
  enabled: 'autoImport.enabled',
  /** The instant the last *successful* search covered up to. */
  lastCheckedAt: 'autoImport.lastCheckedAt',
  /**
   * What the last run found, so the banner and the fetch screen survive a
   * navigation. Candidates, not imports — the check downloads nothing.
   */
  lastRun: 'autoImport.lastRun',
} as const;

/** How far back the very first check looks, having no watermark to start from. */
export const FIRST_CHECK_LOOKBACK_DAYS = 7;

/** The most any one check will search, however long the app has been closed. */
export const MAX_LOOKBACK_DAYS = 30;

/** A second app launch inside this many hours reuses the first one's answer. */
export const MIN_CHECK_INTERVAL_HOURS = 6;

export interface AutoImportConfig {
  enabled: boolean;
  aircraftId?: string;
  /** The watermark: `null` until a check has ever succeeded. */
  lastCheckedAt: string | null;
}

/** The three `autoImport.*` rows, read off one `getAll()` like every other settings consumer. */
export function readAutoImportConfig(rows: Readonly<Record<string, unknown>>): AutoImportConfig {
  const aircraftId = rows[AUTO_IMPORT_KEYS.aircraftId];
  const lastCheckedAt = rows[AUTO_IMPORT_KEYS.lastCheckedAt];
  return {
    enabled: rows[AUTO_IMPORT_KEYS.enabled] === true,
    ...(typeof aircraftId === 'string' && aircraftId !== '' ? { aircraftId } : {}),
    lastCheckedAt:
      typeof lastCheckedAt === 'string' && Number.isFinite(Date.parse(lastCheckedAt))
        ? lastCheckedAt
        : null,
  };
}

/** Whether enough time has passed since the last check to spend another search. */
export function dueForCheck(lastCheckedAt: string | null, now: Date): boolean {
  if (lastCheckedAt === null) {
    return true;
  }
  const since = now.getTime() - Date.parse(lastCheckedAt);
  // A watermark in the future is a clock that moved, not a check from tomorrow.
  if (!Number.isFinite(since) || since < 0) {
    return true;
  }
  return since >= MIN_CHECK_INTERVAL_HOURS * 3_600_000;
}

export interface SearchWindow {
  from: Date;
  to: Date;
}

/**
 * The range one check searches: from the last watermark to now, clamped.
 *
 * The clamp is the whole point. `MAX_LOOKBACK_DAYS` bounds what an app left
 * closed for a season can cost on a metered source, and a window that would
 * end before it starts — a watermark from a clock that has since been wound
 * back — falls back to a single day rather than throwing at the adapter.
 */
export function searchWindow(lastCheckedAt: string | null, now: Date): SearchWindow {
  const to = now;
  const earliest = to.getTime() - MAX_LOOKBACK_DAYS * DAY_MS;
  const parsed = lastCheckedAt === null ? Number.NaN : Date.parse(lastCheckedAt);
  const start = Number.isFinite(parsed)
    ? Math.max(parsed, earliest)
    : to.getTime() - FIRST_CHECK_LOOKBACK_DAYS * DAY_MS;
  return { from: new Date(start >= to.getTime() ? to.getTime() - DAY_MS : start), to };
}
